import React, { useState, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import Step1, { Step1SimData } from './steps/step1'; // SIM
import Step2, { Step2SimData } from './steps/step2'; // Feiras da Agricultura Familiar
import Step3, { Step3SimData } from './steps/step3'; // Feiras Permanentes
import Step4, { Step4SimData } from './steps/step4'; // Feiras Livres
import Step5, { Step5SimData } from './steps/step5'; // Certificação/Identificação
import ContainerPainel from 'components/container_painel';
import { getFormularioSimByUUID, updateFormularioSimByUUID } from 'api/forms/pacote_formularios.api';
import { FormSimHorizontalStepper } from 'components/forms/form_sim_horizontal_stepper';

// Step 1: SIM (Serviço de Inspeção Municipal)
const emptyStep1: Step1SimData = {
  referenceYear: new Date().getFullYear() - 1,
  hasSim: null,
  simActive: null,
  simEstablishments: 0,
  hasVet: null,
  joinedSusaf: null,
  wantsSusaf: null,
};

// Step 2: Feiras da Agricultura Familiar
const emptyStep2: Step2SimData = {
  referenceYear: new Date().getFullYear() - 1,
  hasFamilyFarmersMarket: null,
  numberOfFamilyFarmers: 0,
  annualMarketValue: 0,
  totalFamilyFarmingValue: 0,
  numberOfPermanentMarkets: 0,
  numberOfNonPermanentMarkets: 0,
};

// Step 3: Feiras Permanentes
const emptyStep3: Step3SimData = {
  referenceYear: new Date().getFullYear() - 1,
  permanentMarkets: [],
};

// Step 4: Feiras Livres
const emptyStep4: Step4SimData = {
  referenceYear: new Date().getFullYear() - 1,
  nonPermanentMarkets: [],
};

// Step 5: Certificação/Identificação
const emptyStep5: Step5SimData = {
  hasCertification: null,
  certificationSoldInFairs: null,
  hasMunicipalSeal: null,
  municipalSealName: '',
  wantsMunicipalSeal: null,
  knowsHowToCreateSeal: null,
  referenceYear: new Date().getFullYear() - 1,
};

export const FormularioSIMEditar: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [step1Data, setStep1Data] = useState<Step1SimData>(emptyStep1);
  const [step2Data, setStep2Data] = useState<Step2SimData>(emptyStep2);
  const [step3Data, setStep3Data] = useState<Step3SimData>(emptyStep3);
  const [step4Data, setStep4Data] = useState<Step4SimData>(emptyStep4);
  const [step5Data, setStep5Data] = useState<Step5SimData>(emptyStep5);

  useEffect(() => {
    if (uuid) {
      fetchSimFormData(uuid).then((formData) => {
        setStep1Data(formData.step1);
        setStep2Data(formData.step2);
        setStep3Data(formData.step3);
        setStep4Data(formData.step4);
        setStep5Data(formData.step5);

        console.log("TESTE", formData);
      });
    }
  }, [uuid]);

  const handleBack = () => {
    // Se NÃO possui feira, volta do Step5 para Step2
    if (activeStep === 4 && step2Data.hasFamilyFarmersMarket === false) {
      setActiveStep(1);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const checkStepFilled = (stepData: any, stepType: string) => {
    switch (stepType) {
      case 'step1':
        return stepData.hasSim !== null;
      case 'step2':
        return stepData.hasFamilyFarmersMarket !== null;
      case 'step3':
        return stepData.permanentMarkets && stepData.permanentMarkets.length > 0;
      case 'step4':
        return stepData.nonPermanentMarkets && stepData.nonPermanentMarkets.length > 0;
      case 'step5':
        return stepData.hasCertification !== null;
      default:
        return false;
    }
  };

  const determinePackageStatus = (stepsFilled: boolean[]) => {
    return stepsFilled.every(Boolean) ? 3 : 2;
  };

  const handleSubmit = async () => {
    const stepsFilled = [
      checkStepFilled(step1Data, 'step1'),
      checkStepFilled(step2Data, 'step2'),
      step2Data.hasFamilyFarmersMarket === false
        ? true
        : (step2Data.numberOfPermanentMarkets === 0 && step3Data.permanentMarkets.length === 0) ||
          step3Data.permanentMarkets.length === step2Data.numberOfPermanentMarkets,
      step2Data.hasFamilyFarmersMarket === false
        ? true
        : (step2Data.numberOfNonPermanentMarkets === 0 && step4Data.nonPermanentMarkets.length === 0) ||
          step4Data.nonPermanentMarkets.length === step2Data.numberOfNonPermanentMarkets,
      checkStepFilled(step5Data, 'step5'),
    ];

    const package_status = determinePackageStatus(stepsFilled);

    const payload = {
      has_certification: step5Data.hasCertification,
      certification_sold_in_fairs: step5Data.certificationSoldInFairs,
      has_municipal_seal: step5Data.hasMunicipalSeal,
      municipal_seal_name: step5Data.municipalSealName,
      wants_municipal_seal: step5Data.wantsMunicipalSeal,
      knows_how_to_create_seal: step5Data.knowsHowToCreateSeal,
      reference_year: step1Data.referenceYear,
      has_sim: step1Data.hasSim,
      sim_active: step1Data.simActive,
      sim_establishments: step1Data.simEstablishments,
      has_vet: step1Data.hasVet,
      joined_susaf: step1Data.joinedSusaf,
      wants_susaf: step1Data.wantsSusaf,
      has_family_farmers_market: step2Data.hasFamilyFarmersMarket,
      number_of_family_farmers: step2Data.numberOfFamilyFarmers,
      annual_market_value: step2Data.annualMarketValue,
      total_family_farming_value: step2Data.totalFamilyFarmingValue,
      number_of_permanent_markets: step2Data.numberOfPermanentMarkets,
      number_of_non_permanent_markets: step2Data.numberOfNonPermanentMarkets,
      permanent_markets: step3Data.permanentMarkets,
      non_permanent_markets: step4Data.nonPermanentMarkets,
      package_status,
    };

    if (uuid) {
      await updateFormularioSimByUUID(uuid, payload);
    }

    const shouldNavigate = () => {
      if (
        (step2Data.hasFamilyFarmersMarket === true && activeStep === 4) ||
        (step2Data.hasFamilyFarmersMarket === false && activeStep === 4)
      ) {
        navigate('/pacote-de-formularios');
        return true;
      }
      return false;
    };

    if (shouldNavigate()) return;

    // Lógica para navegação entre os steps
    if (step2Data.hasFamilyFarmersMarket === false && activeStep === 1) {
      setActiveStep(4);
    } else {
      if (activeStep < 4) {
        setActiveStep((prev) => prev + 1);
      }
    }
  };


  return (
    <ContainerPainel pagina="Editar Comercialização" showBackButton={true}>
      <Stack marginX={'4rem'} paddingTop={'2rem'}>
        <FormSimHorizontalStepper
          activeStep={activeStep + 1}
          hasFamilyFarmersMarket={step2Data.hasFamilyFarmersMarket}
          edit={true}
        />

        <Box mt={2}>
          {activeStep === 0 && (
            <Step1
              onNext={handleSubmit}
              onChange={setStep1Data}
              data={step1Data}
            />
          )}

          {activeStep === 1 && (
            <Step2
              onNext={handleSubmit}
              onBack={handleBack}
              onChange={setStep2Data}
              data={step2Data}
            />
          )}

          {/* Se possui feira, Step3, Step4, Step5 normalmente */}
          {step2Data.hasFamilyFarmersMarket === true && activeStep === 2 && (
            <Step3
              onNext={handleSubmit}
              onBack={handleBack}
              onChange={setStep3Data}
              data={step3Data}
              referenceYear={step3Data.referenceYear}
              requiredCount={step2Data.numberOfPermanentMarkets}
            />
          )}
          {step2Data.hasFamilyFarmersMarket === true && activeStep === 3 && (
            <Step4
              onNext={handleSubmit}
              onBack={handleBack}
              onChange={setStep4Data}
              data={step4Data}
              referenceYear={step4Data.referenceYear}
              requiredCount={step2Data.numberOfNonPermanentMarkets}
            />
          )}
          {((step2Data.hasFamilyFarmersMarket === true && activeStep === 4) ||
            (step2Data.hasFamilyFarmersMarket === false && activeStep === 4)) && (
              <Step5
                onNext={handleSubmit}
                onBack={handleBack}
                onChange={setStep5Data}
                data={step5Data}
              />
            )}
        </Box>
      </Stack>
    </ContainerPainel>
  );
};

function getDefaultValue<T>(value: T | undefined | null, defaultValue: T): T {
  return value ?? defaultValue;
}

async function fetchSimFormData(uuid: string) {
  const data = await getFormularioSimByUUID(uuid);

  const currentYear = new Date().getFullYear();
  let referenceYear = getDefaultValue(data?.reference_year, currentYear - 1);

  // Garantir que o year é sempre o ano anterior ao atual
  // Isso corrige dados salvos incorretamente com o ano atual
  if (referenceYear === currentYear) {
    referenceYear = currentYear - 1;
  }

  return {
    step1: {
      referenceYear,
      hasSim: getDefaultValue(data?.has_sim, null),
      simActive: getDefaultValue(data?.sim_active, null),
      simEstablishments: getDefaultValue(data?.sim_establishments, 0),
      hasVet: getDefaultValue(data?.has_vet, null),
      joinedSusaf: getDefaultValue(data?.joined_susaf, null),
      wantsSusaf: getDefaultValue(data?.wants_susaf, null),
    },
    step2: {
      referenceYear,
      hasFamilyFarmersMarket: getDefaultValue(data?.has_family_farmers_market, null),
      numberOfFamilyFarmers: getDefaultValue(data?.number_of_family_farmers, 0),
      annualMarketValue: parseFloat(getDefaultValue(data?.annual_market_value, 0)),
      totalFamilyFarmingValue: parseFloat(getDefaultValue(data?.total_family_farming_value, 0)),
      numberOfPermanentMarkets: getDefaultValue(data?.number_of_permanent_markets, 0),
      numberOfNonPermanentMarkets: getDefaultValue(data?.number_of_non_permanent_markets, 0),
    },
    step3: {
      referenceYear,
      permanentMarkets: getDefaultValue(data?.permanent_markets, []),
    },
    step4: {
      referenceYear,
      nonPermanentMarkets: getDefaultValue(data?.non_permanent_markets, []),
    },
    step5: {
      hasCertification: getDefaultValue(data?.has_certification, null),
      certificationSoldInFairs: getDefaultValue(data?.certification_sold_in_fairs, null),
      hasMunicipalSeal: getDefaultValue(data?.has_municipal_seal, null),
      municipalSealName: getDefaultValue(data?.municipal_seal_name, ''),
      wantsMunicipalSeal: getDefaultValue(data?.wants_municipal_seal, null),
      knowsHowToCreateSeal: getDefaultValue(data?.knows_how_to_create_seal, null),
      referenceYear,
    },
  };
}


export default FormularioSIMEditar;
